import { useEffect } from 'react';

import {
  RiCheckboxCircleFill,
  RiCloseCircleFill,
  RiTimeFill,
} from '@remixicon/react';

import {
  Button,
  Flex,
  Icon,
  Text,
  VisuallyHidden,
} from '@spotify-internal/encore-web';
import { AiLoadingIndicator } from '@spotify/confidence-core-react';

import { EXPERIMENTATION_LOADER_PHRASES } from './experimentationLoaderPhrases';
import {
  getSignupErrorMessage,
  isAccountUnderReviewError,
} from './signupUtils';

import styles from './ExperimentationLoader.module.css';

const RESULT_DISPLAY_MS = 1000;

type ExperimentationLoaderProps = {
  completed?: boolean;
  error?: Error;
  onResultDismissed: () => void;
};

type ExperimentationResult = {
  icon: typeof RiTimeFill;
  semanticColor: 'textWarning' | 'textNegative' | 'textPositive';
  message: string;
  role: 'status' | 'alert';
  dismissible: boolean;
};

const getExperimentationResult = ({
  completed,
  error,
}: Pick<ExperimentationLoaderProps, 'completed' | 'error'>):
  | ExperimentationResult
  | undefined => {
  if (isAccountUnderReviewError(error)) {
    return {
      icon: RiTimeFill,
      semanticColor: 'textWarning',
      message:
        'Your account request is under review, you will get an e-mail once it is approved',
      role: 'status',
      dismissible: true,
    };
  }

  if (error) {
    return {
      icon: RiCloseCircleFill,
      semanticColor: 'textNegative',
      message: getSignupErrorMessage(error),
      role: 'alert',
      dismissible: true,
    };
  }

  if (completed) {
    return {
      icon: RiCheckboxCircleFill,
      semanticColor: 'textPositive',
      message: 'Account created',
      role: 'status',
      dismissible: false,
    };
  }

  return undefined;
};

export const ExperimentationLoader = ({
  completed = false,
  error,
  onResultDismissed,
}: ExperimentationLoaderProps) => {
  const result = getExperimentationResult({ completed, error });
  const shouldAutoDismiss = result?.dismissible === false;

  useEffect(() => {
    if (!shouldAutoDismiss) return undefined;

    const timeout = window.setTimeout(onResultDismissed, RESULT_DISPLAY_MS);
    return () => window.clearTimeout(timeout);
  }, [shouldAutoDismiss, onResultDismissed]);

  if (!result) {
    return (
      <>
        <VisuallyHidden role="status">Creating workspace</VisuallyHidden>
        <div aria-hidden>
          <AiLoadingIndicator
            phrases={EXPERIMENTATION_LOADER_PHRASES}
            spinnerSize="large"
            alignment="center"
          />
        </div>
      </>
    );
  }

  return (
    <Flex
      className={styles.result}
      direction="column"
      alignItems="center"
      gap="looser-2"
      role={result.role}
    >
      <Icon
        as={result.icon}
        size="large"
        semanticColor={result.semanticColor}
        aria-hidden
      />
      <Text variant="bodyMedium" semanticColor="textSubdued">
        {result.message}
      </Text>
      {result.dismissible && (
        <Button
          variant="secondary-bordered"
          size="medium"
          onClick={onResultDismissed}
        >
          Back to workspace setup
        </Button>
      )}
    </Flex>
  );
};
